<template>
  <div class="prenatal-workspace">
    <div v-if="!available" class="text-muted fs-7 py-4">
      <i class="bi bi-lock me-1"></i> O módulo pré-natal não está disponível no seu plano ou perfil de acesso.
    </div>

    <div v-else-if="loading && !detail" class="text-center py-8">
      <span class="spinner-border text-primary"></span>
    </div>

    <template v-else>
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
        <div class="d-flex flex-wrap align-items-center gap-4">
          <select v-if="pregnancies.length > 1" class="form-select form-select-sm w-auto" v-model.number="selectedId" @change="loadDetail">
            <option v-for="pregnancy in pregnancies" :key="pregnancy.id" :value="pregnancy.id">
              {{ pregnancy.status_label }} · DUM {{ formatDateBr(pregnancy.reference_lmp_date) }}
            </option>
          </select>
          <template v-if="detail">
            <div>
              <div class="text-muted fs-8">IG {{ detail.status === 'active' ? 'hoje' : 'no desfecho' }}</div>
              <div class="fw-bolder fs-5">{{ detail.current_gestational_age?.label ?? '—' }}</div>
            </div>
            <div>
              <div class="text-muted fs-8">DPP</div>
              <div class="fw-bolder fs-6">{{ formatDateBr(detail.edd_date) }}</div>
            </div>
            <div>
              <span class="badge" :class="detail.risk === 'high' ? 'badge-light-danger' : 'badge-light-success'">{{ detail.risk_label }}</span>
              <span v-if="detail.fetus_count > 1" class="badge badge-light-info ms-1">{{ detail.fetus_count }} fetos</span>
              <span v-if="detail.status !== 'active'" class="badge badge-light-dark ms-1">{{ detail.status_label }}</span>
            </div>
          </template>
        </div>
        <div class="d-flex gap-2" v-if="!readonly">
          <button v-if="canStore && !activePregnancy" type="button" class="btn btn-sm btn-primary" @click="openPregnancyModal(null)">
            <i class="bi bi-plus-lg me-1"></i> Nova gestação
          </button>
          <button v-if="detail && canUpdate" type="button" class="btn btn-sm btn-light" @click="openPregnancyModal(detail)">
            <i class="bi bi-pencil me-1"></i> Editar
          </button>
          <button v-if="detail?.status === 'active' && canUpdate" type="button" class="btn btn-sm btn-light-danger" @click="openCloseModal">
            <i class="bi bi-flag me-1"></i> Encerrar
          </button>
        </div>
      </div>

      <div v-if="!detail" class="text-center py-6">
        <i class="bi bi-heart-pulse fs-1 text-muted"></i>
        <p class="text-muted fs-7 mt-2 mb-0">Nenhuma gestação registrada para esta paciente.</p>
      </div>

      <ul class="nav nav-tabs nav-line-tabs nav-line-tabs-2x fs-7 fw-bold mb-4 flex-nowrap text-nowrap overflow-auto">
        <li class="nav-item" v-for="tab in visibleTabs" :key="tab.id">
          <a class="nav-link py-2 cursor-pointer" :class="{ active: activeTab === tab.id }" @click="activeTab = tab.id">
            <i :class="tab.icon + ' me-1'"></i>{{ tab.label }}
          </a>
        </li>
      </ul>

      <slot v-if="detail && leadingTab && activeTab === leadingTab.id" name="leading" :detail="detail"/>

      <template v-if="detail && activeTab === 'summary'">
        <div class="row g-3 mb-4">
          <div class="col-md-3 col-6">
            <div class="prenatal-stat rounded p-3 h-100">
              <div class="text-muted fs-8">IG {{ detail.status === 'active' ? 'hoje' : 'no desfecho' }}</div>
              <div class="fw-bolder fs-3">{{ detail.current_gestational_age?.label ?? '—' }}</div>
              <div class="text-muted fs-8" v-if="detail.current_gestational_age">{{ detail.current_gestational_age.trimester }}º trimestre</div>
            </div>
          </div>
          <div class="col-md-3 col-6">
            <div class="prenatal-stat rounded p-3 h-100">
              <div class="text-muted fs-8">DPP</div>
              <div class="fw-bolder fs-4">{{ formatDateBr(detail.edd_date) }}</div>
              <div class="text-muted fs-8">{{ detail.dating_method_label }}</div>
            </div>
          </div>
          <div class="col-md-3 col-6">
            <div class="prenatal-stat rounded p-3 h-100">
              <div class="text-muted fs-8">Risco</div>
              <div class="fw-bolder fs-6" :class="detail.risk === 'high' ? 'text-danger' : ''">{{ detail.risk_label }}</div>
              <div class="text-muted fs-8">{{ detail.fetus_count > 1 ? detail.fetus_count + ' fetos' : 'Gestação única' }}</div>
            </div>
          </div>
          <div class="col-md-3 col-6">
            <div class="prenatal-stat rounded p-3 h-100">
              <div class="text-muted fs-8">IMC pré-gestacional</div>
              <div class="fw-bolder fs-4">{{ formatNumber(detail.baseline?.pre_pregnancy_bmi) }}</div>
              <div class="text-muted fs-8">{{ detail.baseline?.bmi_category_label ?? 'Sem peso/altura' }}</div>
            </div>
          </div>
        </div>

        <div v-for="alert in sortedAlerts" :key="alert.code + alert.message" class="alert py-2 px-3 mb-2 fs-7" :class="alertClass(alert.severity)">
          <i class="bi bi-exclamation-triangle me-1"></i>{{ alert.message }}
        </div>

        <div class="row g-3 mt-1">
          <div class="col-md-6">
            <div class="border rounded p-3 h-100 cursor-pointer" @click="activeTab = 'exams'">
              <div class="fw-bolder fs-7 mb-2">Exames do protocolo</div>
              <span v-for="(count, status) in examCounts" :key="status" class="badge me-1 mb-1" :class="checklistStatus(status).badge">
                {{ checklistStatus(status).label }}: {{ count }}
              </span>
            </div>
          </div>
          <div class="col-md-6">
            <div class="border rounded p-3 h-100 cursor-pointer" @click="activeTab = 'vaccines'">
              <div class="fw-bolder fs-7 mb-2">Vacinas</div>
              <span v-for="item in detail.vaccine_checklist" :key="item.vaccine" class="badge me-1 mb-1" :class="checklistStatus(item.status, 'vaccine').badge">
                {{ item.label.split(' (')[0] }}: {{ checklistStatus(item.status, 'vaccine').label }}
              </span>
            </div>
          </div>
          <div class="col-md-6" v-if="lastVisit">
            <div class="border rounded p-3 h-100">
              <div class="fw-bolder fs-7 mb-2">Última consulta · {{ formatDateBr(lastVisit.visit_date) }} ({{ lastVisit.gestational_age.label }})</div>
              <div class="fs-7 d-flex flex-wrap gap-3">
                <span>Peso: <b>{{ formatNumber(lastVisit.weight_kg, 'kg') }}</b></span>
                <span>PA: <b>{{ lastVisit.blood_pressure ?? '—' }}</b></span>
                <span>AU: <b>{{ formatNumber(lastVisit.fundal_height_cm, 'cm') }}</b></span>
                <span>BCF: <b>{{ fetalHeartRateText(lastVisit) }}</b></span>
              </div>
            </div>
          </div>
          <div class="col-md-6" v-if="detail.obstetric_history">
            <div class="border rounded p-3 h-100 cursor-pointer" @click="activeTab = 'history'">
              <div class="fw-bolder fs-7 mb-2">Histórico obstétrico</div>
              <div class="fs-5 fw-bolder text-primary">{{ detail.obstetric_history.gpa_notation }}</div>
            </div>
          </div>
        </div>
      </template>

      <template v-if="detail && activeTab === 'visits'">
        <div v-if="!detail.visits.length" class="text-muted fs-7 text-center py-4">
          Nenhuma consulta registrada.
        </div>
        <div v-else class="table-responsive">
          <table class="table table-sm table-row-dashed align-middle fs-7">
            <thead>
            <tr class="text-muted fs-8 text-uppercase">
              <th>Data</th>
              <th>IG</th>
              <th>Peso</th>
              <th>PA</th>
              <th>AU</th>
              <th>BCF</th>
              <th>Apresentação</th>
              <th>Edema</th>
              <th>MF</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="visit in visitsDesc" :key="visit.id">
              <td>{{ formatDateBr(visit.visit_date) }}</td>
              <td>{{ visit.gestational_age.label }}</td>
              <td>{{ formatNumber(visit.weight_kg) }}</td>
              <td>{{ visit.blood_pressure ?? '—' }}</td>
              <td>{{ formatNumber(visit.fundal_height_cm) }}</td>
              <td>{{ fetalHeartRateText(visit) }}</td>
              <td>{{ presentationText(visit) }}</td>
              <td>{{ visit.edema_label ?? '—' }}</td>
              <td>{{ visit.fetal_movement_label ?? '—' }}</td>
              <td>
                <i v-if="visit.alerts?.length" class="bi bi-exclamation-triangle-fill"
                   :class="visit.alerts.some((alert) => alert.severity === 'critical') ? 'text-danger' : 'text-warning'"
                   :title="visit.alerts.map((alert) => alert.message).join('\n')"></i>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </template>

      <PrenatalExamsTab v-if="detail && activeTab === 'exams'" :key="'exams-' + detail.id"
                        :base-url="detailUrl" :readonly="readonly || !canUpdate" :can-delete="canDelete" @changed="loadDetail"/>
      <PrenatalUltrasoundsTab v-if="detail && activeTab === 'ultrasounds'" :key="'us-' + detail.id"
                              :base-url="detailUrl" :fetus-count="detail.fetus_count" :readonly="readonly || !canUpdate"
                              :can-delete="canDelete" @changed="loadDetail"/>
      <PrenatalVaccinesTab v-if="detail && activeTab === 'vaccines'" :key="'vac-' + detail.id"
                           :base-url="detailUrl" :readonly="readonly || !canUpdate" :can-delete="canDelete" @changed="loadDetail"/>
      <PrenatalChartsTab v-if="detail && activeTab === 'charts'" :key="'charts-' + detail.id + '-' + detail.visits.length"
                         :base-url="detailUrl"/>
      <ObstetricHistoryTab v-if="activeTab === 'history'"
                           :professional-id="professionalId" :patient-id="patientId" :place-id="placeId"
                           :readonly="readonly || !canUpdate" @changed="loadDetail"/>
    </template>

    <PregnancyFormModal
        v-if="available"
        :open="pregnancyModalOpen"
        :pregnancy="editingPregnancy"
        :professional-id="professionalId"
        :patient-id="patientId"
        :place-id="placeId"
        @close="pregnancyModalOpen = false"
        @saved="onPregnancySaved"/>

    <Teleport to="body">
      <div v-if="closeForm" class="modal fade show d-block" tabindex="-1" @click.self="closeForm = null">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Encerrar gestação</h5>
              <button type="button" class="btn-close" @click="closeForm = null"></button>
            </div>
            <div class="modal-body">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label fs-7">Desfecho</label>
                  <select class="form-select form-select-sm" :class="{ 'is-invalid': closeErrors.status }" v-model="closeForm.status">
                    <option v-for="option in closeStatusOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                  </select>
                  <small class="text-danger" v-if="closeErrors.status">{{ closeErrors.status }}</small>
                </div>
                <div class="col-md-6">
                  <label class="form-label fs-7">Data</label>
                  <input type="date" class="form-control form-control-sm" :class="{ 'is-invalid': closeErrors.outcome_date }" v-model="closeForm.outcome_date">
                  <small class="text-danger" v-if="closeErrors.outcome_date">{{ closeErrors.outcome_date }}</small>
                </div>
                <div class="col-md-6" v-if="closeForm.status !== 'transferred'">
                  <label class="form-label fs-7">Tipo</label>
                  <select class="form-select form-select-sm" :class="{ 'is-invalid': closeErrors.outcome_type }" v-model="closeForm.outcome_type">
                    <option value="">—</option>
                    <option v-for="option in outcomeTypeOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                  </select>
                  <small class="text-danger" v-if="closeErrors.outcome_type">{{ closeErrors.outcome_type }}</small>
                </div>
                <div class="col-12">
                  <label class="form-label fs-7">Observações</label>
                  <textarea class="form-control form-control-sm" rows="2" v-model="closeForm.notes"></textarea>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-sm btn-light" @click="closeForm = null">Cancelar</button>
              <button type="button" class="btn btn-sm btn-danger" :disabled="closing" @click="closePregnancy">
                <span v-if="closing" class="spinner-border spinner-border-sm me-1"></span> Encerrar
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="closeForm" class="modal-backdrop fade show"></div>
    </Teleport>
  </div>
</template>

<script>
import http from '../../../helpers/http';
import Alert from '../../../helpers/alert';
import PregnancyFormModal from './PregnancyFormModal.vue';
import PrenatalExamsTab from './workspace/PrenatalExamsTab.vue';
import PrenatalUltrasoundsTab from './workspace/PrenatalUltrasoundsTab.vue';
import PrenatalVaccinesTab from './workspace/PrenatalVaccinesTab.vue';
import PrenatalChartsTab from './workspace/PrenatalChartsTab.vue';
import ObstetricHistoryTab from './workspace/ObstetricHistoryTab.vue';
import { canDeletePrenatal, canStorePrenatal, canUpdatePrenatal, prenatalAvailable } from './prenatalAccess';
import {
  CLOSE_STATUS_OPTIONS,
  OUTCOME_TYPE_OPTIONS,
  alertClass,
  buildPayload,
  checklistCounts,
  checklistStatus,
  fetalHeartRateText,
  fetusLabel,
  formatDateBr,
  formatNumber,
  patientBaseUrl,
  pregnancyUrl,
  sortAlerts,
  todayIso,
  validationErrors,
} from './prenatal';

const TABS = [
  { id: 'summary', label: 'Resumo', icon: 'bi bi-speedometer2', needsPregnancy: true },
  { id: 'visits', label: 'Consultas', icon: 'bi bi-calendar2-heart', needsPregnancy: true },
  { id: 'exams', label: 'Exames', icon: 'bi bi-droplet', needsPregnancy: true },
  { id: 'ultrasounds', label: 'USG', icon: 'bi bi-soundwave', needsPregnancy: true },
  { id: 'vaccines', label: 'Vacinas', icon: 'bi bi-shield-plus', needsPregnancy: true },
  { id: 'charts', label: 'Gráficos', icon: 'bi bi-graph-up', needsPregnancy: true },
  { id: 'history', label: 'Histórico obstétrico', icon: 'bi bi-journal-medical', needsPregnancy: false },
];

export default {
  name: 'PrenatalWorkspace',
  components: {
    PregnancyFormModal,
    PrenatalExamsTab,
    PrenatalUltrasoundsTab,
    PrenatalVaccinesTab,
    PrenatalChartsTab,
    ObstetricHistoryTab,
  },
  props: {
    professionalId: { type: [Number, String], required: true },
    patientId: { type: [Number, String], required: true },
    placeId: { type: [Number, String], required: true },
    readonly: Boolean,
    initialPregnancyId: { type: [Number, String], default: null },
    leadingTab: { type: Object, default: null },
  },
  emits: ['loaded'],
  data() {
    return {
      available: prenatalAvailable(),
      canStore: canStorePrenatal(),
      canUpdate: canUpdatePrenatal(),
      canDelete: canDeletePrenatal(),
      loading: false,
      pregnancies: [],
      selectedId: null,
      detail: null,
      activeTab: this.leadingTab?.id ?? 'summary',
      pregnancyModalOpen: false,
      editingPregnancy: null,
      closeForm: null,
      closeErrors: {},
      closing: false,
      closeStatusOptions: CLOSE_STATUS_OPTIONS,
      outcomeTypeOptions: OUTCOME_TYPE_OPTIONS,
    };
  },
  computed: {
    activePregnancy() {
      return this.pregnancies.find((pregnancy) => pregnancy.status === 'active') ?? null;
    },
    detailUrl() {
      return this.detail ? pregnancyUrl(this.professionalId, this.patientId, this.detail.id) : '';
    },
    defaultTab() {
      return this.leadingTab?.id ?? 'summary';
    },
    visibleTabs() {
      const tabs = this.leadingTab ? [{ ...this.leadingTab, needsPregnancy: true }, ...TABS] : TABS;

      return tabs.filter((tab) => !tab.needsPregnancy || this.detail);
    },
    sortedAlerts() {
      return sortAlerts(this.detail?.alerts);
    },
    examCounts() {
      return checklistCounts(this.detail?.exam_checklist);
    },
    lastVisit() {
      const visits = this.detail?.visits ?? [];

      return visits.length ? visits[visits.length - 1] : null;
    },
    visitsDesc() {
      return [...(this.detail?.visits ?? [])].reverse();
    },
  },
  mounted() {
    if (this.available) {
      this.load();
    }
  },
  methods: {
    alertClass,
    checklistStatus,
    fetalHeartRateText,
    formatDateBr,
    formatNumber,
    presentationText(visit) {
      if (visit.fetuses?.length) {
        return visit.fetuses
          .filter((fetus) => fetus.fetal_presentation_label)
          .map((fetus) => `${fetusLabel(fetus.fetus_number).replace('Feto ', '')} ${fetus.fetal_presentation_label}`)
          .join(' · ') || '—';
      }

      return visit.fetal_presentation_label ?? '—';
    },
    async load(preferredId = null) {
      this.loading = true;
      try {
        const response = await http.get(`${patientBaseUrl(this.professionalId, this.patientId)}/pregnancies`, {
          params: { id_place: this.placeId },
        });
        this.pregnancies = response.data ?? [];
        const initialId = this.pregnancies.some((pregnancy) => pregnancy.id === Number(this.initialPregnancyId))
          ? Number(this.initialPregnancyId)
          : null;
        this.selectedId = preferredId ?? initialId ?? this.activePregnancy?.id ?? this.pregnancies[0]?.id ?? null;

        if (this.selectedId) {
          await this.loadDetail();
        } else {
          this.detail = null;
          this.activeTab = 'history';
          this.$emit('loaded', null);
        }
      } catch (error) {
        if (error.response?.status !== 403) {
          Alert.toastError('Falha ao carregar o pré-natal.');
        }
      } finally {
        this.loading = false;
      }
    },
    async loadDetail() {
      if (!this.selectedId) {
        return;
      }

      try {
        const response = await http.get(pregnancyUrl(this.professionalId, this.patientId, this.selectedId));
        this.detail = response.data;
        if (!this.visibleTabs.some((tab) => tab.id === this.activeTab)) {
          this.activeTab = this.defaultTab;
        }
        this.$emit('loaded', this.detail);
      } catch (_) {
        Alert.toastError('Falha ao carregar a gestação.');
      }
    },
    openPregnancyModal(pregnancy) {
      this.editingPregnancy = pregnancy;
      this.pregnancyModalOpen = true;
    },
    reload() {
      return this.loadDetail();
    },
    async onPregnancySaved(pregnancy) {
      this.pregnancyModalOpen = false;
      this.activeTab = this.defaultTab;
      await this.load(pregnancy.id);
    },
    openCloseModal() {
      this.closeErrors = {};
      this.closeForm = { status: 'delivered', outcome_date: todayIso(), outcome_type: '', notes: '' };
    },
    async closePregnancy() {
      this.closing = true;
      this.closeErrors = {};

      try {
        await http.post(pregnancyUrl(this.professionalId, this.patientId, this.detail.id, '/close'), buildPayload(this.closeForm));
        this.closeForm = null;
        Alert.toastSuccess('Gestação encerrada.');
        await this.load(this.detail.id);
      } catch (error) {
        this.closeErrors = validationErrors(error);
        if (!Object.keys(this.closeErrors).length) {
          Alert.toastError(error.response?.data?.message || 'Falha ao encerrar a gestação.');
        }
      } finally {
        this.closing = false;
      }
    },
  },
};
</script>

<style scoped>
.prenatal-stat {
  background: #f8f5ff;
}
</style>
